import Button from 'components/Button';
import FormActionGroup from 'components/FormActionGroup';
import {GooglePlaceAutoComplete} from 'components/GooglePlaceAutoComplete';
import TextField from 'components/TextField';
import Typography from 'components/Typography';
import {
  cc_format,
  dd_format,
} from 'pages/UserForm/CardInformation/CardInformation';
import {Controller, useForm} from 'react-hook-form';
import toast, {Toaster} from 'react-hot-toast';
import styles from './HeartlandForm.module.scss';
import {useState} from 'react';
import axios from 'axios';
import api from 'api';
import {get} from 'lodash';
import Card from 'components/Card';
import CardContent from 'components/CardContent';
import {CreditCardIcon} from 'pages/PaymentForm/icons/credit-card';
import {getCardType} from 'helpers/getCardTypeIcon';

type FormData = {
  card_number: string;
  expiry_date: string;
  secure_code: string;
  cardholder_name: string;
  billing_address: {
    address_line_1: string;
    city: string;
    state: string;
    zipcode: string;
    latitude: number;
    longitude: number;
  };
  amount_due: string;
};

type Props = {
  heartlandPublicKey: string;
  paymentId: string;
  amountDue: string;
  onSuccess: () => void;
};

const HeartlandForm = ({
  heartlandPublicKey,
  paymentId,
  amountDue,
  onSuccess,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const {
    control,
    setValue,
    handleSubmit,
    formState: {errors},
  } = useForm<FormData>();
  const handlePayment = async (data: FormData) => {
    setLoading(true);
    try {
      const card = {
        card: {
          number: data.card_number.replace(/\s+/g, ''),
          cvc: data.secure_code,
          exp_month: data.expiry_date.split('/')[0],
          exp_year: '20' + data.expiry_date.split('/')[1],
        },
        object: 'token',
        token_type: 'supt',
      };
      const {data: response} = await axios.post(
        `https://api.heartlandportico.com/SecureSubmit.v1/api/token?api_key=${heartlandPublicKey}`,
        card
      );
      const body = {
        id: paymentId,
        card_number: response.card.number,
        card_type: getCardType(data.card_number),
        cardholder_name: data.cardholder_name,
        token: response.token_value,
        amount_due: amountDue,
        billing_address: data.billing_address,
      };
      await api.post('/payment/links/', body);
      onSuccess();
      setLoading(false);
    } catch (error) {
      const message = get(
        error,
        'response.data.detail',
        'Error creating payment'
      );
      toast.error(message || 'Error creating payment');
      setLoading(false);
    }
  };
  return (
    <Card className={styles.card}>
      <CardContent pt="sm" className={styles.cardContent}>
        <Typography className={styles.title} variant="h4">
          <CreditCardIcon className={styles.cardIcon} />
          Payment Method
        </Typography>
        <Controller
          name="card_number"
          control={control}
          rules={{
            required: 'Please, fill card number',
            minLength: {
              value: 18,
              message: 'Please, fill card number',
            },
          }}
          render={({field, fieldState}) => (
            <TextField
              label="Card Number"
              onChange={(event) => {
                setValue('card_number', cc_format(event.target.value));
              }}
              wrapperClass={styles.googleInput}
              placeholder="**** **** **** 1234"
              hasError={false}
              value={field.value}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="expiry_date"
          control={control}
          rules={{
            required: 'Please, fill expiration date',
            minLength: {
              value: 5,
              message: 'Please, fill expiration date',
            },
          }}
          render={({field, fieldState}) => (
            <TextField
              label="Expiration date"
              wrapperClass={styles.googleInput}
              placeholder="mm/yy"
              hasError={false}
              onChange={(event) => {
                setValue('expiry_date', dd_format(event.target.value));
              }}
              value={field.value}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="secure_code"
          control={control}
          rules={{
            required: 'Please, fill security code',
            minLength: {
              value: 3,
              message: 'Please, fill security code',
            },
          }}
          render={({field, fieldState}) => (
            <TextField
              maxLength={4}
              label="Security code"
              value={field.value}
              wrapperClass={styles.googleInput}
              placeholder="CVC"
              hasError={false}
              onChange={(event) => {
                setValue('secure_code', event.target.value.replace(/\D/g, ''));
              }}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="cardholder_name"
          control={control}
          rules={{required: 'Please, fill holder name'}}
          render={({field, fieldState}) => (
            <TextField
              value={field.value}
              label="Card holder name"
              wrapperClass={styles.googleInput}
              placeholder="Enter card holder name"
              hasError={false}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <GooglePlaceAutoComplete
          label="Billing address"
          placeholder="Address"
          onPlaceSelected={(data) => {
            setValue('billing_address', {
              address_line_1: data.addressLine1,
              city: data.city,
              state: data.state,
              zipcode: data.zipcode,
              latitude: data.lat,
              longitude: data.lon,
            });
          }}
          error={errors?.billing_address?.address_line_1?.message}
          options={{
            types: ['address'],
          }}
        />
        <FormActionGroup className={styles.actionWrapper}>
          <Button
            loading={loading}
            type="submit"
            className={styles.button}
            onClick={handleSubmit(handlePayment)}
          >
            Pay Now
          </Button>
        </FormActionGroup>
        <Toaster />
      </CardContent>
    </Card>
  );
};

export default HeartlandForm;
